import { useEffect, useRef, useState } from 'react';
import { useOrganization } from '../useOrganization';
import { DisplayColorPalette } from './DisplayColorPalette';
import styles from './organization-palette.module.less';
import ButtonText from '../../components/button/button-text/ButtonText';
import { Subtitle } from '../../components/typography/Heading';
import RoundedButtonSecondary from '../../components/button/rounded-button-secondary/RoundedButtonSecondary';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import {
    BAER,
    FROST_30,
    FROST_70,
    getSupportColor1,
    getSupportColor2,
    LYNG,
    MULTE,
    NORDLYS,
    SKOG,
    SOL,
    VANN,
    VILLBLOMST,
} from '../theme/organization-theme-model';
import { useDispatch } from 'react-redux';
import { fetchOrganization, updateOrganizationInfo } from '../organization-actions';
import { DID_CHANGE_ORGANIZATION_COLOR } from '../../constants';
import { ScrollableModalStickyBottom } from '../../components/scrollable-modal-sticky-bottom/ScrollableModalStickyBottom';
import AddButton from '../../components/button/add-button/AddButton';
import RoundedButtonPrimary from '../../components/button/rounded-button-primary/RoundedButtonPrimary';
import useQueryString from '../../components/hooks/useQueryString';

export const ChangePaletteModal = () => {
    const organization = useOrganization();
    const [{ showPalette }] = useQueryString();
    const [open, setOpen] = useState(false);
    const [showCustomColor, setShowCustomColor] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [currentColor, setCurrentColor] = useState(organization?.themeColor);
    const dispatch = useDispatch();
    const colorPicker = useRef();

    const colors = [VANN, BAER, NORDLYS, SKOG, SOL, MULTE, VILLBLOMST, LYNG];

    useEffect(() => {
        const hasCustomColor = organization?.themeColor && colors.indexOf(organization.themeColor) === -1;
        setCurrentColor(organization?.themeColor || '');
        setShowCustomColor(hasCustomColor);
        if (!hasCustomColor) {
            hideColorPicker();
        }
    }, [organization?.themeColor, open]);

    useEffect(() => {
        if (showColorPicker) {
            colorPicker.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [showColorPicker]);

    useEffect(() => {
        if (showPalette === 'true') {
            setOpen(true);
        }
    }, [showPalette]);

    const displayColorPicker = () => {
        setShowColorPicker(true);
    };

    const hideColorPicker = () => {
        setShowColorPicker(false);
    };

    const updateOrganizationColor = () => {
        dispatch(updateOrganizationInfo({ id: organization.id, profile: { themeColor: currentColor } }));
        setOpen(false);
        hideColorPicker();
    };

    const cancelColor = () => {
        dispatch(fetchOrganization(organization.id, true));
        setOpen(false);
        hideColorPicker();
    };

    const setColor = (color) => {
        setCurrentColor(color);
        setShowCustomColor(!color || colors.indexOf(color) === -1);

        dispatch({
            type: DID_CHANGE_ORGANIZATION_COLOR,
            data: { id: organization.id, themeColor: color },
        });
    };

    return (
        <div className={styles.menuItem}>
            <style>
                {`
                .react-colorful {
                    width: auto;
                    border-radius: 0.5rem;
                }

                .react-colorful__saturation {
                    border-radius: 0.5rem;
                }
                .react-colorful__hue {
                    margin-top: 1rem;
                    border-radius: 0.5rem;
                    height: 1rem;
                }
            `}
            </style>
            {organization?.themeColor ? (
                <DisplayColorPalette primaryColor={organization?.themeColor} onClick={() => setOpen(true)} />
            ) : (
                <DisplayColorPalette
                    primaryColor={SKOG}
                    secondaryColor={FROST_30}
                    tertiaryColor={FROST_70}
                    onClick={() => setOpen(true)}
                />
            )}
            <ButtonText onClick={() => setOpen(true)} className={styles.buttonText} tightFocus>
                Fargepalett
            </ButtonText>
            <ScrollableModalStickyBottom
                isOpen={open}
                onRequestClose={cancelColor}
                title={'Velg din fargepalett'}
                wrapperClass={styles.modalWrapper}
                footerComponent={
                    <>
                        <RoundedButtonSecondary onClick={cancelColor}>Avbryt</RoundedButtonSecondary>
                        <RoundedButtonPrimary onClick={updateOrganizationColor}>Lagre</RoundedButtonPrimary>
                    </>
                }
            >
                <div className={styles.container}>
                    <span className={styles.lead}>
                        Øk gjenkjennbarheten blant giverne ved å få frem deres merkevare 🎨
                    </span>
                    <span>
                        Fargepaletten du velger vil brukes på deres organisasjonsside, aksjonssider, og delingsbilder
                        for innsamlinger startet for deres organisasjon.
                    </span>
                    <Subtitle className={styles.subtitle}>Ferdige paletter</Subtitle>
                    <div className={styles.palettes}>
                        <DisplayColorPalette
                            primaryColor={SKOG}
                            secondaryColor={FROST_30}
                            tertiaryColor={FROST_70}
                            selected={!currentColor}
                            setColor={() => setColor('')}
                        />
                        {colors.map((color) => (
                            <DisplayColorPalette
                                key={color}
                                primaryColor={color}
                                setColor={(c) => {
                                    setColor(c);
                                    setShowCustomColor(false);
                                }}
                                current={currentColor}
                            />
                        ))}
                    </div>
                    <Subtitle className={styles.subtitle}>Din palett</Subtitle>
                    <span>Generer din egen fargepalett ut fra din organisasjons hovedfarge. </span>
                    {!showCustomColor && (
                        <AddButton
                            onClick={() => {
                                setShowCustomColor(true);
                                displayColorPicker();
                            }}
                        >
                            Legg til farge
                        </AddButton>
                    )}
                    {showCustomColor && (
                        <div className={styles.chosenCustomColors}>
                            <span>Hovedfarge</span>
                            <span>Nyanser</span>
                            <div
                                className={[styles.previewColor, currentColor ? styles.chosenColor : ''].join(' ')}
                                style={{ backgroundColor: currentColor }}
                                onClick={displayColorPicker}
                            ></div>
                            <div className={styles.variantColors}>
                                <div
                                    className={[styles.previewColor, currentColor ? styles.chosenColor : ''].join(' ')}
                                    style={{ backgroundColor: getSupportColor1(currentColor) }}
                                    onClick={displayColorPicker}
                                ></div>
                                <div
                                    className={[styles.previewColor, currentColor ? styles.chosenColor : ''].join(' ')}
                                    style={{ backgroundColor: getSupportColor2(currentColor) }}
                                    onClick={displayColorPicker}
                                ></div>
                            </div>
                        </div>
                    )}
                    <div ref={colorPicker}>
                        {showColorPicker && (
                            <div className={styles.colorPicker}>
                                <HexColorPicker color={currentColor} onChange={setColor} />
                                <HexColorInput
                                    className={styles.input}
                                    color={currentColor}
                                    onChange={setColor}
                                    prefixed
                                />
                            </div>
                        )}
                    </div>
                </div>
            </ScrollableModalStickyBottom>
        </div>
    );
};
